import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import { ChevronUpDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import SearchIcon from 'icons/SearchIcon';
import Link from 'next/link';
import { TeamAndDetails } from '@thera-hr/api-sdk/api/resources/api/resources/thera/resources/company';

import Button from 'components/app-components/ui/button';
import Settings from 'icons/Settings';
import { AdminRoutes } from 'routes';
import { getInitials } from 'utils/string';
import { usePermissions } from 'hooks/usePermissions';
import { useUser } from 'context/user';
import AllTeamsIcon from 'icons/AllTeamsIcon';
import { useTeamsSWR } from 'hooks/useTeamsSWR';
import { getWhiteLabelTeamTermPlural, getWhiteLabelTeamTerm } from 'utils/whiteLabelConfig';
import CrossIcon from 'icons/CrossIcon';

const TeamMenu = ({
  selectedTeamId,
  setSelectedTeamId,
  teams,
  isSidebarCollapsed,
}: {
  setSelectedTeamId: (teamId: string) => void;
  selectedTeamId: string;
  teams: Record<string, TeamAndDetails>;
  isSidebarCollapsed?: boolean;
}) => {
  const router = useRouter();
  const { showAllTeamsContracts } = useUser();
  const { isOrgAdmin, isHRSpecialist } = usePermissions();
  const isAllSelected = showAllTeamsContracts;
  const { teams: allTeams } = useTeamsSWR();
  const teamTerm = getWhiteLabelTeamTerm();
  const teamTermPlural = getWhiteLabelTeamTermPlural();
  const selectedTeam = allTeams[selectedTeamId];
  const selectedTeamName = selectedTeam?.name;
  const selectedTeamInitials = getInitials(selectedTeamName);

  // if there's no valid team present for the selectedTeamId, set selectedTeamId to null (all teams)
  useEffect(() => {
    if (!selectedTeam) setSelectedTeamId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTeam]);

  // sort selected team to the top
  const sortedTeams = useMemo(() => {
    const selectedTeam = teams[selectedTeamId];
    const teamsCopy: Record<string, TeamAndDetails> = JSON.parse(JSON.stringify(teams));
    delete teamsCopy[selectedTeamId];
    const isValidTeam = selectedTeamId && selectedTeam;
    return {
      ...(isValidTeam && { [selectedTeamId]: selectedTeam }), // this shows the selected team in the list and isn't required in the case where All Teams is selected
      ...teamsCopy,
    };
  }, [teams, selectedTeamId]);

  const goToManageTeam = (teamId: string) => {
    if (teamId) {
      router.push({
        pathname: AdminRoutes.ManageTeam,
        query: { teamId },
      });
      return;
    }
    router.push(AdminRoutes.Teams); // if all teams is selected, go to the teams settings page
  };

  const resetSearch = () => {
    setSearchQuery('');
    setIsSearchActive(false);
  };

  const handleTeamSelection = (teamId: string) => {
    resetSearch();
    if (teamId === 'all') {
      setSelectedTeamId(null);
    } else {
      setSelectedTeamId(teamId);
    }
  };

  const { yourTeams, otherTeams } = useMemo(() => {
    const yourTeams: Record<string, TeamAndDetails> = {};
    const otherTeams: Record<string, TeamAndDetails> = {};

    Object.keys(allTeams).forEach((teamId) => {
      if (teams[teamId]) {
        yourTeams[teamId] = allTeams[teamId];
      } else {
        otherTeams[teamId] = allTeams[teamId];
      }
    });

    return { yourTeams, otherTeams };
  }, [teams, allTeams]);

  const renderTeamItem = (teamId: string, team: TeamAndDetails) => {
    const isSelected = !showAllTeamsContracts && teamId === selectedTeamId;
    const teamInitials = getInitials(team?.name);

    return (
      <MenuItem
        key={teamId}
        as="a"
        className={`flex items-center justify-between px-4 py-3 text-sm hover:cursor-pointer hover:bg-gray-100 ${
          isSelected ? 'bg-gray-100 font-medium text-therablue' : 'font-normal text-neutral-700'
        }`}
        onClick={() => handleTeamSelection(teamId)}
      >
        <div className="flex w-full items-center overflow-hidden text-left">
          <span className="mr-2 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-therablue-dark text-[10px] font-medium text-white">
            {teamInitials}
          </span>
          <span className="overflow-hidden truncate text-ellipsis whitespace-nowrap">{team?.name}</span>
        </div>
        {(isOrgAdmin || isHRSpecialist) && (
          <Button
            variant="plain"
            onClick={(e) => {
              e.stopPropagation();
              goToManageTeam(teamId);
            }}
          >
            <span className="flex w-6 items-center justify-center">
              <Settings className="h-4 w-4 fill-neutral-400 group-hover:fill-neutral-900" />
            </span>
          </Button>
        )}
      </MenuItem>
    );
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const filteredYourTeams = useMemo(() => {
    if (!searchQuery) return yourTeams;
    const q = searchQuery.toLowerCase();
    return Object.fromEntries(Object.entries(yourTeams).filter(([, team]) => team?.name?.toLowerCase().includes(q)));
  }, [yourTeams, searchQuery]);

  const filteredOtherTeams = useMemo(() => {
    if (!searchQuery) return otherTeams;
    const q = searchQuery.toLowerCase();
    return Object.fromEntries(Object.entries(otherTeams).filter(([, team]) => team?.name?.toLowerCase().includes(q)));
  }, [otherTeams, searchQuery]);

  const filteredSortedTeams = useMemo(() => {
    if (!searchQuery) return sortedTeams;
    const q = searchQuery.toLowerCase();
    return Object.fromEntries(Object.entries(sortedTeams).filter(([, team]) => team?.name?.toLowerCase().includes(q)));
  }, [sortedTeams, searchQuery]);

  const searchHeader = (
    <div className="mb-1 flex items-center justify-between px-4 py-2">
      {isSearchActive ? (
        <div className="relative flex w-full items-center gap-1">
          <input
            autoFocus
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${teamTermPlural.toLowerCase()}...`}
            className="form-input !border-0 !bg-transparent !px-2 !py-1"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSearchQuery('');
              setIsSearchActive(false);
            }}
            className="absolute right-2 top-1/2 shrink-0 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
          >
            <CrossIcon className="size-3" />
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-neutral-600">{teamTermPlural.toUpperCase()}</p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSearchActive(true);
            }}
            className="text-neutral-400 hover:text-neutral-600"
          >
            <SearchIcon className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );

  const menuButtonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [menuRect, setMenuRect] = useState<DOMRect | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isHovered) resetSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered]);

  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
    if (menuButtonRef.current) {
      setMenuRect(menuButtonRef.current.getBoundingClientRect());
    }
  }, []);

  const handleMouseLeave = useCallback((e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget;
    if (
      relatedTarget instanceof Node &&
      (menuButtonRef.current?.contains(relatedTarget) || tooltipRef.current?.contains(relatedTarget))
    ) {
      return;
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100);
  }, []);

  const dropdownContent = (
    <>
      {searchHeader}
      <MenuItem
        key={'all'}
        as="a"
        className={`flex items-center justify-between border-y border-gray-200 px-4 py-3 text-sm hover:cursor-pointer hover:bg-gray-100 ${
          isAllSelected ? 'bg-gray-100 font-medium text-therablue' : 'font-normal text-neutral-700'
        }`}
        onClick={() => handleTeamSelection('all')}
      >
        <div className="flex items-center text-left">
          <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-md bg-neutral-100">
            <AllTeamsIcon className={`h-4 w-4 ${isAllSelected ? '[&>path#allteamsPath]:fill-therablue' : ''}`} />
          </span>
          <span>All {teamTermPlural}</span>
        </div>
      </MenuItem>

      {isOrgAdmin || isHRSpecialist ? (
        <>
          {Object.keys(filteredYourTeams)?.length > 0 && (
            <>
              <div className="mt-1 flex items-center justify-between px-3 py-1">
                <p className="text-xs font-medium uppercase text-neutral-400">Your {teamTermPlural}</p>
                <Link
                  href={AdminRoutes.Teams}
                  className="text-xs font-semibold text-therablue hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Manage
                </Link>
              </div>
              {Object.keys(filteredYourTeams).map((teamId) => renderTeamItem(teamId, filteredYourTeams[teamId]))}
            </>
          )}

          {Object.keys(filteredOtherTeams)?.length > 0 && (
            <>
              <p className="mt-1 px-3 py-1 text-left text-xs font-medium uppercase text-neutral-400">
                Other {teamTermPlural}
              </p>
              {Object.keys(filteredOtherTeams).map((teamId) => renderTeamItem(teamId, filteredOtherTeams[teamId]))}
            </>
          )}
          <Link href={AdminRoutes.AddTeam} className="flex w-full items-center">
            <div className="flex w-full items-center border-t border-gray-200 px-4 py-3 hover:bg-gray-100">
              <span className="mr-2 flex w-6 items-center justify-center">
                <PlusIcon className="h-5 w-4 fill-therablue-dark" />
              </span>
              <span className="flex-1 text-left text-sm font-normal text-neutral-800 hover:text-neutral-600">
                Add {teamTerm}
              </span>
            </div>
          </Link>
        </>
      ) : (
        Object.keys(filteredSortedTeams)?.length > 0 &&
        Object.keys(filteredSortedTeams).map((teamId) => renderTeamItem(teamId, filteredSortedTeams[teamId]))
      )}
    </>
  );

  if (isSidebarCollapsed) {
    return (
      <div
        className="relative mb-3 flex w-full"
        ref={menuButtonRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {typeof document !== 'undefined' &&
          createPortal(
            <div
              ref={tooltipRef}
              className={`fixed z-[9999] max-h-[360px] w-64 overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 transition-[opacity,transform] duration-200 before:absolute before:-left-4 before:top-0 before:h-full before:w-4 dark:ring-white/10 ${
                isHovered
                  ? 'pointer-events-auto translate-x-0 opacity-100'
                  : 'pointer-events-none -translate-x-2 opacity-0'
              }`}
              style={{
                top: menuRect ? menuRect.top : 0,
                left: menuRect ? menuRect.right + 8 : 88,
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {searchHeader}
              <a
                className={`flex items-center justify-between border-y border-gray-200 px-4 py-3 text-sm hover:cursor-pointer hover:bg-gray-100 ${
                  isAllSelected ? 'bg-gray-100 font-medium text-therablue' : 'font-normal text-neutral-700'
                }`}
                onClick={() => {
                  handleTeamSelection('all');
                  setIsHovered(false);
                }}
              >
                <div className="flex items-center text-left">
                  <span className="mr-2 flex h-6 w-6 items-center justify-center rounded-md bg-neutral-100">
                    <AllTeamsIcon
                      className={`h-4 w-4 ${isAllSelected ? '[&>path#allteamsPath]:fill-therablue' : ''}`}
                    />
                  </span>
                  <span>All {teamTermPlural}</span>
                </div>
              </a>
              {isOrgAdmin || isHRSpecialist ? (
                <>
                  {Object.keys(filteredYourTeams)?.length > 0 && (
                    <>
                      <div className="mt-1 flex items-center justify-between px-3 py-1">
                        <p className="text-xs font-medium uppercase text-neutral-400">Your {teamTermPlural}</p>
                        <Link
                          href={AdminRoutes.Teams}
                          className="text-xs font-semibold text-therablue hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsHovered(false);
                          }}
                        >
                          Manage
                        </Link>
                      </div>
                      {Object.keys(filteredYourTeams).map((teamId) => {
                        const team = filteredYourTeams[teamId];
                        const isSelected = !showAllTeamsContracts && teamId === selectedTeamId;
                        const teamInitials = getInitials(team?.name);
                        return (
                          <a
                            key={teamId}
                            className={`flex items-center justify-between px-4 py-3 text-sm hover:cursor-pointer hover:bg-gray-100 ${
                              isSelected ? 'bg-gray-100 font-medium text-therablue' : 'font-normal text-neutral-700'
                            }`}
                            onClick={() => {
                              handleTeamSelection(teamId);
                              setIsHovered(false);
                            }}
                          >
                            <div className="flex w-full items-center overflow-hidden text-left">
                              <span className="mr-2 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-therablue-dark text-[10px] font-medium text-white">
                                {teamInitials}
                              </span>
                              <span className="overflow-hidden truncate text-ellipsis whitespace-nowrap">
                                {team?.name}
                              </span>
                            </div>
                            <Button
                              variant="plain"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToManageTeam(teamId);
                                setIsHovered(false);
                              }}
                            >
                              <span className="mr-2 flex w-6 items-center justify-center">
                                <Settings className="h-4 w-4 fill-neutral-400 group-hover:fill-neutral-900" />
                              </span>
                            </Button>
                          </a>
                        );
                      })}
                    </>
                  )}
                  {Object.keys(filteredOtherTeams)?.length > 0 && (
                    <>
                      <p className="mt-1 px-3 py-1 text-left text-xs font-medium uppercase text-neutral-400">
                        Other {teamTermPlural}
                      </p>
                      {Object.keys(filteredOtherTeams).map((teamId) => {
                        const team = filteredOtherTeams[teamId];
                        const isSelected = !showAllTeamsContracts && teamId === selectedTeamId;
                        const teamInitials = getInitials(team?.name);
                        return (
                          <a
                            key={teamId}
                            className={`flex items-center justify-between px-4 py-3 text-sm hover:cursor-pointer hover:bg-gray-100 ${
                              isSelected ? 'bg-gray-100 font-medium text-therablue' : 'font-normal text-neutral-700'
                            }`}
                            onClick={() => {
                              handleTeamSelection(teamId);
                              setIsHovered(false);
                            }}
                          >
                            <div className="flex w-full items-center overflow-hidden text-left">
                              <span className="mr-2 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-therablue-dark text-[10px] font-medium text-white">
                                {teamInitials}
                              </span>
                              <span className="overflow-hidden truncate text-ellipsis whitespace-nowrap">
                                {team?.name}
                              </span>
                            </div>
                            <Button
                              variant="plain"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToManageTeam(teamId);
                                setIsHovered(false);
                              }}
                            >
                              <span className="mr-2 flex w-6 items-center justify-center">
                                <Settings className="h-4 w-4 fill-neutral-400 group-hover:fill-neutral-900" />
                              </span>
                            </Button>
                          </a>
                        );
                      })}
                    </>
                  )}
                  <Link
                    href={AdminRoutes.AddTeam}
                    className="flex w-full items-center"
                    onClick={() => setIsHovered(false)}
                  >
                    <div className="flex w-full items-center border-t border-gray-200 px-4 py-3 hover:bg-gray-100">
                      <span className="mr-2 flex w-6 items-center justify-center">
                        <PlusIcon className="h-5 w-4 fill-therablue-dark" />
                      </span>
                      <span className="flex-1 text-left text-sm font-normal text-neutral-800 hover:text-neutral-600">
                        Add {teamTerm}
                      </span>
                    </div>
                  </Link>
                </>
              ) : (
                Object.keys(filteredSortedTeams)?.length > 0 &&
                Object.keys(filteredSortedTeams).map((teamId) => {
                  const team = filteredSortedTeams[teamId];
                  const isSelected = !showAllTeamsContracts && teamId === selectedTeamId;
                  const teamInitials = getInitials(team?.name);
                  return (
                    <a
                      key={teamId}
                      className={`flex items-center px-4 py-3 text-sm hover:cursor-pointer hover:bg-gray-100 ${
                        isSelected ? 'bg-gray-100 font-medium text-therablue' : 'font-normal text-neutral-700'
                      }`}
                      onClick={() => {
                        handleTeamSelection(teamId);
                        setIsHovered(false);
                      }}
                    >
                      <span className="mr-2 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-therablue-dark text-[10px] font-medium text-white">
                        {teamInitials}
                      </span>
                      <span className="truncate">{team?.name}</span>
                    </a>
                  );
                })
              )}
            </div>,
            document.body,
          )}
        <a className="flex w-full flex-1 cursor-pointer items-center rounded-md border border-neutral-200 px-1.5 py-1.5">
          <div className="flex h-[24px] max-w-full flex-1 items-center overflow-hidden truncate text-ellipsis text-left text-sm font-medium text-neutral-500">
            {showAllTeamsContracts ? (
              <span className="mx-auto flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-neutral-100">
                <AllTeamsIcon className="h-4 w-4" />
              </span>
            ) : (
              <span className="mx-auto flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-therablue-dark text-[10px] font-medium text-white">
                {selectedTeamInitials}
              </span>
            )}
          </div>
        </a>
      </div>
    );
  }

  return (
    <Menu>
      <div className="relative mb-3 flex w-full" ref={menuButtonRef}>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
          afterLeave={resetSearch}
        >
          <MenuItems
            modal={false}
            className="absolute right-0 z-[100] mt-10 max-h-[360px] w-full max-w-xs overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:ring-white/10"
          >
            {dropdownContent}
          </MenuItems>
        </Transition>
        <MenuButton as={Fragment}>
          <a className="flex w-full flex-1 cursor-pointer items-center rounded-md border border-neutral-200 px-1.5 py-1.5">
            <div
              className={`flex h-[24px] max-w-full flex-1 items-center overflow-hidden truncate text-ellipsis text-left text-sm font-medium text-neutral-500 ${
                isSidebarCollapsed ? '' : 'md:max-w-[200px]'
              }`}
            >
              {showAllTeamsContracts ? (
                <span
                  className={`mr-2 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-neutral-100 ${
                    isSidebarCollapsed ? 'mx-auto' : ''
                  }`}
                >
                  <AllTeamsIcon className="h-4 w-4" />
                </span>
              ) : (
                <span
                  className={`mr-2 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-md bg-therablue-dark text-[10px] font-medium text-white ${
                    isSidebarCollapsed ? 'mx-auto' : ''
                  }`}
                >
                  {selectedTeamInitials}
                </span>
              )}
              {!isSidebarCollapsed && (
                <span className="truncate">{showAllTeamsContracts ? `All ${teamTermPlural}` : selectedTeamName}</span>
              )}
            </div>
            {!isSidebarCollapsed && <ChevronUpDownIcon className="ml-1 h-4 w-4 text-neutral-500" aria-hidden="true" />}
          </a>
        </MenuButton>
      </div>
    </Menu>
  );
};

export default TeamMenu;
